import loading from 'assets/pictures/loading.gif'
import { push } from '@lagunovsky/redux-react-router'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { orderBy, round, take } from 'lodash'
import { getLabel } from '../../utils/StoreUtils'
import { getDateWithHourString, getTimestamp } from '../../utils/DateUtil'
import MonitoredMaterielPortletPanel from '../../materiel/components/dashboard/MonitoredMaterielPortletPanel'
import { getCampaignIcon, getCampaignNameCell, getCampaignProgress, getCampaignsWithBookmarks, TableProgressBar } from '../../campaign/utils/CampaignUtils'
import useCampaignBookmarks from '../../utils/customHook/useCampaignBookmarks'
import { getColorCampaign } from '../../utils/CampaignUtil'
import { componentHasHabilitations } from '../../utils/HabilitationUtil'
import {
    H_MAT_MODULE,
    H_PIEZO_MODULE,
} from '../../account/constants/AccessRulesConstants'
import HabilitationRequired from '../../utils/components/HabilitationRequired'
import moment from 'moment'
import { STATION_TYPE_CONSTANT, STATION_TYPE_NAME } from 'station/constants/StationConstants'
import UserThunk from 'administration/components/user/actions/UserThunk'
import CampaignThunk from 'campaign/actions/CampaignThunk'
import PiezometryThunk from 'piezometry/actions/PiezometryThunk'
import useStateProgress from 'utils/customHook/useStateProgress'
import { PiezometryActionConstant } from 'piezometry/reducers/PiezometryReducer'
import useAccountSetting from '../../utils/customHook/useAccountSetting'
import StationAction from '../../station/actions/StationAction'
import MapSituationPie from '../../station/components/mapSituation/MapSituationPie'
import { Card, CardContent, CircularProgress, Grid, Icon, IconButton, Stack } from '@mui/material'
import { ALIGN, NewTable } from '../../components/datatable/NewTable'
import { getStationDesignation } from '../../utils/StationUtils'
import CampaignPortletPanel from 'campaign/components/CampaignPortletPanel'
import useAbortController from 'utils/customHook/useAbortController'
import CampaignAction from 'campaign/actions/CampaignAction'
import { hasValue } from 'utils/NumberUtil'
import { darkBlue } from '../../utils/constants/ColorTheme'
import ShowChartIcon from '@mui/icons-material/ShowChart'
import { CardTitle } from '../../components/card/NewCard'
import ProgressBar from '../../components/progress/ProgressBar'

const PiezometryCard = () => {
    const {
        userBookmarks,
        piezometersLight,
        piezometryCampaigns,
        piezometryCampaignsProgress,
        piezometryMaxSituation,
        piezometryRawSituation,
        monitoredMateriels,
        citiesIndex,
        userBookmarksStatus,
        piezometersLightStatus,
        piezometryCampaignsStatus,
        contacts,
        monitoredStations,
    } = useSelector(store => ({
        userBookmarks: store.UserReducer.userBookmarks,
        piezometersLight: store.PiezometryReducer.piezometersLight,
        piezometryCampaigns: store.PiezometryReducer.piezometryCampaigns,
        piezometryCampaignsProgress: store.PiezometryReducer.piezometryCampaignsProgress,
        piezometryMaxSituation: store.PiezometryReducer.piezometryMaxSituation,
        piezometryRawSituation: store.PiezometryReducer.piezometryRawSituation,
        monitoredMateriels: store.MaterielReducer.monitoredMateriels,
        citiesIndex: store.CityReducer.citiesIndex,
        userBookmarksStatus: store.DataManagerReducer.user.userBookmarksStatus,
        piezometersLightStatus: store.DataManagerReducer.piezometry.piezometersLightStatus,
        piezometryCampaignsStatus: store.DataManagerReducer.campaign.piezometryCampaignsStatus,
        contacts: store.ContactReducer.contacts,
        monitoredStations: store.EventsReducer.monitoredStations,
    }), shallowEqual)

    const { progress: progressBar, isLoaded: dataLoaded } = useStateProgress([userBookmarksStatus, piezometersLightStatus, piezometryCampaignsStatus])

    const mapSituationWidgetObj = useAccountSetting('MAP_SITUATION_DASHBOARD_WIDGET', setting => JSON.parse(setting ?? '{}'))

    const dispatch = useDispatch()
    const [mapSituationStats, setMapSituationStats] = useState([])

    const bookmarks = useMemo(() => {
        const piezoBookmarks = userBookmarks.filter(bk => bk.stationType === STATION_TYPE_CONSTANT.PIEZOMETRY) // piezo
        return piezoBookmarks.map(b => b.identifiant)
    }, [userBookmarks])

    const stationsForTable = useMemo(() => {
        const bookmarksStations = bookmarks.map(code => piezometersLight.find(s => s.code === code))
            .filter(p => !!p).map(s => ({ ...s, isBookmark: true }))
        const monitored = monitoredStations.filter(s => s.type === STATION_TYPE_CONSTANT.PIEZOMETRY && piezometersLight.some(s2 => s.id === s2.id)).map(s => ({ ...s, isMonitored: true }))
        const reduced = [...bookmarksStations, ...monitored].reduce((acc, s) => {
            const found = acc.find(s2 => s.id === s2.id)
            if (found) {
                return [...acc.filter(s2 => s2.id !== s.id), { ...s, ...found }]
            }
            return [...acc, s]
        }, [])
        return orderBy(reduced, [s => s.isMonitored && s.isBookmark ? 2 : (s.isMonitored ? 1 : 0), 'name'], ['desc', 'asc'])
    }, [piezometersLight, bookmarks, monitoredStations])

    useEffect(() => {
        dispatch(UserThunk.fetchBookmarks())
        dispatch(PiezometryThunk.fetchPiezometersLight())
        dispatch(CampaignThunk.fetchPiezometryCampaigns())
    }, [])


    const { bookmarkIds: campaignBookmarkIds } = useCampaignBookmarks(STATION_TYPE_NAME.piezometry)

    const orderedCampaigns = useMemo(() => {
        const currentCampaigns = orderBy(piezometryCampaigns.filter(c => c.statut === 2), c => getTimestamp(c.beginningApplication), 'desc')
        return getCampaignsWithBookmarks(piezometryCampaigns, currentCampaigns, campaignBookmarkIds)
    }, [piezometryCampaigns, campaignBookmarkIds])

    const {
        controllerRef,
        initController,
    } = useAbortController()

    useEffect(() => {
        initController()
        if (orderedCampaigns.length) {
            dispatch(CampaignAction.getCampaignProgressSequentially(STATION_TYPE_NAME.piezometry, take(orderedCampaigns, 3), controllerRef.current.signal))
        }
    }, [orderedCampaigns, controllerRef])

    useEffect(() => {
        const piezoMapIds = Object.keys(mapSituationWidgetObj).filter(key => key.startsWith(STATION_TYPE_NAME.piezometry) && !!mapSituationWidgetObj[key]).map(k => parseInt(k.replace('piezometry-', '')))
        if (piezoMapIds.length) {
            Promise.all(piezoMapIds.map(id => StationAction.promiseMapSituationStats(STATION_TYPE_NAME.piezometry, id))).then(jsonTab => setMapSituationStats(jsonTab.filter(t => t.length)))
        }
    }, [mapSituationWidgetObj])

    useEffect(() => {
        const criterias = { date: moment().valueOf(), nbDays: 30 }
        const ids = stationsForTable.map(b => b.id)
        dispatch(PiezometryThunk.fetchPiezometryRawSituation({ criterias, piezometerIds: ids }))
    }, [bookmarks, dispatch, stationsForTable, piezometersLight?.length])

    useEffect(() => () => dispatch(PiezometryActionConstant.resetPiezometryRawSituation()), [])

    const data = useMemo(() => ({
        stations: piezometersLight,
        bookmarks,
        icon: 'show_chart',
        campaigns: orderedCampaigns.map(campaign => {
            const {
                nbStation = 0,
                nbStationValidated = 0,
                id,
            } = piezometryCampaignsProgress.find(cp => cp.id === campaign.id) || {}
            const progress = getCampaignProgress(nbStationValidated, nbStation)
            return {
                id: campaign.id,
                qualificationTableColor: {
                    color: getColorCampaign(false, progress, 2),
                },
                nullValue: getCampaignIcon(campaign.campaignType, 'small'),
                name: getCampaignNameCell(campaign.name, campaignBookmarkIds.includes(campaign.id)),
                nbStations: nbStation,
                startDate: campaign.beginningApplication,
                endDate: campaign.endingApplication,
                progression: hasValue(id) ? <TableProgressBar nb={nbStationValidated} nbTotal={nbStation} progress={progress} /> : <CircularProgress size={25} />,
                referent: getLabel(contacts, campaign.contactCode, 'mnemonique'),
            }
        }),
        stationType: STATION_TYPE_CONSTANT.PIEZOMETRY,
        habilitation: componentHasHabilitations(H_PIEZO_MODULE),
        monitoredMateriels: monitoredMateriels.filter(m => m.typeSite === STATION_TYPE_CONSTANT.PIEZOMETRY),
    }), [bookmarks, orderedCampaigns, contacts, monitoredMateriels, piezometersLight, piezometryCampaignsProgress, campaignBookmarkIds])

    const bookmarksStationPanel = useMemo(() => {
        if (!stationsForTable.length && !mapSituationStats.length) {
            return null
        }
        const tableData = stationsForTable.map(station => {
            const cityFound = citiesIndex[station.townCode]
            const foundSituation = piezometryRawSituation.find(s => s.piezometerId == station.id) || piezometryMaxSituation.find(s => s.piezometerId == station.id)
            const colorLastMeasure = foundSituation?.thresholdColor ? foundSituation.thresholdColor : 'white'
            return {
                id: station.id,
                code: getStationDesignation(station),
                city: cityFound ? `${cityFound.name} [${cityFound.code}]` : station.townCode,
                name: station.name,
                lastMeasure: {
                    value: foundSituation ? `${round(foundSituation.value, 2)} m - ${getDateWithHourString(foundSituation.lastMeasureDate, foundSituation.lastMeasureHour)}` : '',
                    sortValue: foundSituation ? round(foundSituation.value, 2) : '',
                    color: ['black', '#000000'].includes(colorLastMeasure) ? 'white' : colorLastMeasure,
                    tooltip: foundSituation && foundSituation.threshold,
                },
                comment: station.eventComment,
                nullValue: {
                    value: '',
                    align: 'center',
                    leftIcon: station.isMonitored && station.isBookmark ? {
                        icon: 'notificationsNone',
                        color: 'red',
                        tooltip: i18n.oversight,
                    } : undefined,
                    rightIcon: {
                        icon: station.isBookmark ? 'star' : 'notificationsNone',
                        color: station.isBookmark ? 'orange' : 'red',
                        tooltip: station.isBookmark ? i18n.bookmark : i18n.oversight,
                    },
                },
            }
        })
        const mapSituationPies = mapSituationStats.map(tab => {
            return (
                <Grid key={tab} size={4}><MapSituationPie stats={tab} st={STATION_TYPE_NAME.piezometry} /></Grid>
            )
        })
        const mapSituationCard = mapSituationPies.length ? (
            <Grid container justifyContent='center' alignItems='center' spacing={3} className='padding-bottom-1'>
                {mapSituationPies}
            </Grid>
        ) : undefined
        return (
            <>
                {mapSituationCard}
                {
                    tableData.length ? (
                        <NewTable
                            headers={ ['nullValue', 'code', 'city', 'name', 'lastMeasure', 'comment']}
                            rows={tableData}
                            onClickRow={s => dispatch(push(`/station/piezometry/${s.id}`))}
                        />
                    ) : null
                }
            </>
        )
    }, [citiesIndex, stationsForTable, piezometryMaxSituation, piezometryRawSituation, mapSituationStats])

    const bookmarksPanel = useMemo(() => {
        if (stationsForTable.length > 0 || mapSituationStats.length) {
            return bookmarksStationPanel
        }
        return null
    }, [bookmarksStationPanel, stationsForTable.length, mapSituationStats.length])


    const headerIcon = (
        <IconButton
            sx={{
                color: '#000',
                fontSize: '1.8rem',
                padding: '1px',
            }}
            onClick={() => dispatch(push('/piezometry/campaign'))}
        >
            <Icon fontSize='inherit'>nature_people</Icon>
        </IconButton>
    )

    const campaignsPanel = useMemo(() => {
        if (data.campaigns && data.campaigns.length > 0) {
            return (
                <CampaignPortletPanel
                    campaigns={data.campaigns}
                    stationType={STATION_TYPE_NAME.piezometry}
                    headers={[
                        { key: 'qualificationTableColor', value: headerIcon, width: 26, sticky: ALIGN.LEFT },
                        { key: 'campaignIcon', value: '' }, { key: 'name', value: i18n.campaign },
                        'nbStations', 'startDate', 'endDate', 'progression',
                    ]}
                />
            )
        }
        return null
    }, [data.campaigns])

    if (dataLoaded) {
        if (!(data.habilitation && ((data.bookmarks.length > 0 && data.stations.length > 0) || mapSituationStats.length || data.campaigns.length > 0 || data.monitoredMateriels.length > 0 || data?.exploitations?.length > 0)) || !(bookmarksPanel || campaignsPanel || mapSituationStats.length)) {
            return null
        }
        return (
            <Card>
                <CardTitle
                    title={i18n[STATION_TYPE_NAME.piezometry]}
                    icon={<ShowChartIcon fontSize='large'/>}
                    color={darkBlue}
                    onClick={() => dispatch(push(`/${data.redirect || STATION_TYPE_NAME.piezometry}`))}
                />
                <Stack spacing={1}>
                    {bookmarksPanel}
                    {campaignsPanel}
                    {data.monitoredMateriels.length ? (
                        <HabilitationRequired habilitation={H_MAT_MODULE}>
                            <MonitoredMaterielPortletPanel
                                noHeader
                                stationType={data.stationType}
                            />
                        </HabilitationRequired>
                    ) : null}
                </Stack>
            </Card>
        )
    }
    return (
        <Card>
            <CardContent>
                <Grid
                    container
                    justifyContent='center'
                    alignItems='center'
                    direction='column'
                    sx={{ pt: 2.5 }}
                >

                    <img src={loading} />
                    <Grid size={3}>
                        <ProgressBar
                            withMessage
                            message={i18n.piezometryCardInProgress}
                            progress={progressBar}
                        />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

export default PiezometryCard